import * as echarts from "echarts";
import { formatNumber } from "src/helpers/api/sharedFunction";
import React, { useEffect, useRef } from "react";
import { OhlcvDataPoint } from "src/helpers";
import moment from "moment-timezone";

const AreaChart = ({
  areaData,
  darkMode,
  isExpand,
  isAnalyzeStock,
  isDashboard,
}: {
  areaData: OhlcvDataPoint[];
  darkMode: boolean;
  isExpand?: boolean;
  isAnalyzeStock?: boolean;
  isDashboard?: boolean;
}) => {
  const chartRef = useRef<HTMLDivElement>(null);
  const chartInstance = useRef<echarts.EChartsType | null>(null);

  // Scope legend preference dynamically based on the current module's route
  const pathname =
    typeof window !== "undefined" ? window.location.pathname : "";
  const moduleName = pathname.split("/")[1] || "default";

  const LEGEND_STORAGE_KEY = `area_legend_selected_${moduleName}`;
  const LEGEND_SYNC_EVENT = `area-legend-changed-${moduleName}`;

  const getSavedLegendSelected = (): Record<string, boolean> => {
    try {
      const saved = localStorage.getItem(LEGEND_STORAGE_KEY);
      return saved ? JSON.parse(saved) : {};
    } catch {
      return {};
    }
  };

  const saveLegendSelected = (selected: Record<string, boolean>) => {
    try {
      localStorage.setItem(LEGEND_STORAGE_KEY, JSON.stringify(selected));
      // Notify the sibling chart instance (card ↔ expand modal)
      window.dispatchEvent(
        new CustomEvent(LEGEND_SYNC_EVENT, { detail: selected }),
      );
    } catch {
      // ignore storage errors
    }
  };

  const dates = areaData.map((item) => {
    return moment.utc(item?.date).tz("Asia/Kolkata").format("YYYY-MM-DD");
  });

  const closes = areaData.map((item) => item.price);
  const average =
    closes.reduce((sum, val) => sum + val, 0) / (closes.length || 1);
  const defaultLegendSelected: Record<string, boolean> = {
    UpperBB: true,
    LowerBB: true,
    SMA20: true,
    ClosingPrice: true,
    Volume: true,
  };

  const savedLegend = getSavedLegendSelected();
  // Merge: start from defaults, override with whatever was saved
  const mergedLegendSelected = { ...defaultLegendSelected, ...savedLegend };

  const option = {
    backgroundColor: "transparent",
    tooltip: {
      trigger: "axis",
      formatter: (
        params: {
          axisValue: string;
          seriesType: string;
          color: string;
          seriesName: string;
          data: number;
          value: number;
        }[],
      ) => {
        const date = params[0].axisValue;
        const dateIST = moment.tz(date, "Asia/Kolkata").format("DD MMM YYYY");
        let html = `<div style="margin-bottom: 4px;">${dateIST}</div><table style="width: 100%; border-collapse: collapse;">`;

        // Sort to place ClosingPrice at the top
        const sortedParams = [...params].sort((a, b) => {
          if (a.seriesName === "ClosingPrice") return -1;
          if (b.seriesName === "ClosingPrice") return 1;
          return 0;
        });

        sortedParams.forEach((p) => {
          const color =
            p.seriesName === "Volume"
              ? darkMode
                ? "#25c9e6ff"
                : "#16547eff"
              : p.color;

          const numValue = Number(p.value);
          if (isNaN(numValue) || p.value === null || p.value === undefined)
            return;

          const formattedValue = numValue.toLocaleString("en-US", {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          });

          html += `<tr>
            <td style="padding-right: 16px; text-align: left;">
              <span style="color:${color}">●</span> ${p.seriesName}:  ${formattedValue}
            </td>
           
          </tr>`;
        });
        html += `</table>`;
        return html;
      },
      confine: true,
    },

    legend: {
      data: ["ClosingPrice", "UpperBB", "LowerBB", "SMA20", "Volume"],
      selected: mergedLegendSelected,
      itemWidth: 20,
      itemHeight: 9,
      left: "center",
      textStyle: {
        fontWeight: "normal",
      },
    },
    grid: [
      {
        left: 2,
        right: 20,
        bottom: isExpand ? "5%" : "6%",
        height: isExpand ? "80%" : "65%",
        containLabel: true,
      },
      {
        left: 32,
        right: 20,
        bottom: isExpand ? 39 : 35,
        height: isExpand ? "20%" : "15%",
        containLabel: true,
      },
    ],

    xAxis: [
      {
        type: "category",
        data: dates,
        boundaryGap: false,
        axisLabel: {
          hideOverlap: true,
          formatter: (value: string) => new Date(value).getFullYear(),
          interval: (index: number) => {
            if (index === 0) return true;
            const prev = new Date(dates[index - 1]);
            const curr = new Date(dates[index]);
            return curr.getFullYear() !== prev.getFullYear();
          },
        },
        axisTick: { alignWithLabel: true },
        splitLine: { show: false },
      },
      {
        type: "category",
        gridIndex: 1,
        data: dates,
        boundaryGap: false,
        axisLine: { show: false },
        axisTick: { show: false },
        axisLabel: {
          show: false,
        },
        splitLine: { show: false },
        axisPointer: {
          label: {
            formatter: (params: { value: string }) => {
              const date = new Date(params.value);
              const day = date.getDate().toString().padStart(2, "0");
              const month = date.toLocaleString("default", { month: "short" });
              const year = date.getFullYear();
              return `${day} ${month} ${year}`;
            },
          },
        },
      },
    ],
    yAxis: [
      {
        type: "value",
        scale: true,
        splitLine: {
          show: false,
        },
        min: 0,
        axisLabel: {
          formatter: (value: number) => formatNumber(value, true),
        },
      },
      {
        gridIndex: 1,
        type: "value",
        axisLine: { show: false },
        axisTick: { show: false },
        axisLabel: { show: false },
        splitLine: { show: false },
      },
    ],
    dataZoom: [
      {
        type: "inside",
        xAxisIndex: [0, 1],
        minSpan: 30,
        zoomLock: isExpand ? false : true,
      },
      {
        show: false,
        xAxisIndex: [0, 1],
        type: "slider",
        minSpan: 30,
        zoomLock: isExpand ? false : true,
      },
    ],
    series: [
      {
        name: "UpperBB",
        type: "line",
        color: "#198754",
        smooth: true,
        data: areaData.map((d) => d.ubb),
        symbolSize: (params: { dataIndex: number }) => {
          return params && params.dataIndex % 12 === 0 ? 3 : 0;
        },
        lineStyle: {
          width: 1,
        },
        label: {
          show: false,
          position: "top",
          formatter: (params: { dataIndex: number; data: number }) => {
            return params && params.dataIndex % 6 === 0
              ? `${Math.round(params.data)}K`
              : "";
          },
          fontSize: 10,
          color: "#198754",
        },
        showSymbol: false, // make it true if want to show pointers
        labelLayout: {
          moveOverlap: "shiftY",
        },
      },
      {
        name: "LowerBB",
        type: "line",
        color: "#fa7b17",
        smooth: true,
        data: areaData.map((d) => d.lbb),
        symbolSize: (params: { dataIndex: number }) => {
          return params && params.dataIndex % 12 === 0 ? 3 : 0;
        },
        lineStyle: {
          width: 1,
        },
        label: {
          show: false,
          position: "top",
          formatter: (params: { dataIndex: number; data: number }) => {
            return params && params.dataIndex % 6 === 0
              ? `${Math.round(params.data)}K`
              : "";
          },
          fontSize: 10,
          color: "#fa7b17",
        },
        showSymbol: false,
        labelLayout: {
          moveOverlap: "shiftY",
        },
      },
      {
        name: "SMA20",
        type: "line",
        color: "#FD1050",
        smooth: true,
        data: areaData.map((d) => d["20daysmaline"]),

        symbolSize: (params: { dataIndex: number }) => {
          return params && params.dataIndex % 12 === 0 ? 3 : 0;
        },
        lineStyle: {
          width: 1,
        },
        label: {
          show: false,
          position: "top",
          formatter: (params: { dataIndex: number; data: number }) => {
            return params && params.dataIndex % 6 === 0
              ? `${Math.round(params.data)}K`
              : "";
          },
          fontSize: 10,
          color: "#FD1050",
        },
        showSymbol: false,
        labelLayout: {
          moveOverlap: "shiftY",
        },
      },
      {
        name: "ClosingPrice",
        type: "line",
        color: darkMode ? "#ffffff" : "#000000",
        symbol: "none",
        smooth: true,
        lineStyle: {
          width: 1,
        },
        areaStyle: {
          opacity: darkMode ? 0.3 : 1,
          color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
            { offset: 0, color: "rgba(187, 195, 199, 0.76)" },
            { offset: 1, color: "rgba(255, 255, 255, 0)" },
          ]),
        },
        data: closes,
      },
      {
        name: "Average",
        type: "line",
        data: new Array(closes.length).fill(average),
        lineStyle: {
          color: "#72b2e9ff",
          width: 0.5,
        },
        symbol: "none",
        markPoint: {
          symbol: "circle",
          symbolSize: 6,
          itemStyle: {
            color: "#466788ff",
          },
          label: {
            show: true,
            color: darkMode ? "#ffffff" : "#477ba8ff",
            backgroundColor: darkMode
              ? "rgba(0,0,0,0.6)"
              : "rgba(255,255,255,0.8)",
            borderColor: "#6c757d",
            borderWidth: 0.2,
            borderRadius: 4,
            padding: [4, 8],
            position: "top",
            align: "left",
            distance: 6,
            formatter: "Average Price: " + average.toFixed(0),
          },
          data: [
            {
              coord: [dates[0], average],
              value: average.toFixed(2),
            },
          ],
        },
      },

      {
        name: "Volume",
        type: "bar",
        xAxisIndex: 1,
        yAxisIndex: 1,
        data: areaData.map((d) => Number(d.volume)),
        color: darkMode
          ? new echarts.graphic.LinearGradient(0, 0, 0, 1, [
              { offset: 0, color: "#25c9e6ff" },
              { offset: 1, color: "#16547eff" },
            ])
          : new echarts.graphic.LinearGradient(0, 0, 0, 1, [
              { offset: 0, color: "#16547eff" },
              { offset: 1, color: "#25c9e6ff" },
            ]),

        itemStyle: {
          color: () =>
            new echarts.graphic.LinearGradient(0, 0, 0, 1, [
              { offset: 0, color: "#16547eff" },
              { offset: 1, color: "#25c9e6ff" },
            ]),
          opacity: 0.85,
        },
      },
    ],
  };

  useEffect(() => {
    if (chartRef.current) {
      if (!chartInstance.current) {
        chartInstance.current = echarts.init(
          chartRef.current,
          darkMode ? "dark" : "light",
        );

        // Listen for legend toggles and persist to localStorage
        chartInstance.current.on("legendselectchanged", (params: unknown) => {
          const legendEvent = params as {
            type: string;
            name: string;
            selected: Record<string, boolean>;
          };
          saveLegendSelected(legendEvent.selected);
        });
      }
      chartInstance.current.setOption(option);
    }

    const handleLegendSync = (e: Event) => {
      const selected = (e as CustomEvent<Record<string, boolean>>).detail;
      chartInstance.current?.setOption({ legend: { selected } }, false);
    };
    window.addEventListener(LEGEND_SYNC_EVENT, handleLegendSync);

    const handleResize = () => {
      chartInstance.current?.resize();
    };

    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener(LEGEND_SYNC_EVENT, handleLegendSync);
      window.removeEventListener("resize", handleResize);
      chartInstance.current?.dispose();
      chartInstance.current = null;
    };
  }, [areaData, darkMode]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div
      ref={chartRef}
      id="area-chart"
      className={`w-full ${isExpand ? "h-full min-h-0" : isAnalyzeStock || isDashboard ? "h-full min-h-0" : "h-[30vh]"}`}
    />
  );
};

export default React.memo(AreaChart);
